import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { AddArticlePayload, Article, Category, Tag } from '../types/Document';
import { NpiArticle } from 'models/Article';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BASE_URL, BASE_URL_V2 } from 'shared/constants';

export const useDocumentApi = () => {
  const { axios } = useAxios(BASE_URL);
  const { axios: axiosV2 } = useAxios(BASE_URL_V2);

  /**
   * Fetches all articles.
   * @returns {Promise<Article[]>} Array of articles sorted by id descending.
   * @throws {Error} If the API request fails.
   */
  const fetchArticles = async (): Promise<Article[]> => {
    try {
      const res = await axiosV2.get(`/document`);
      if (res?.status === 200) {
        // Return camelCase response directly from v2 API
        const articles = res?.data || [];
        return articles.sort((a: Article, b: Article) => {
          return b?.id - a?.id;
        });
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch articles'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches a single article by id.
   * @param {number} id - The article id.
   * @returns {Promise<Article>} Article data.
   * @throws {Error} If the API request fails.
   */
  const fetchArticleDetail = async (id: number): Promise<Article> => {
    try {
      const res = await axiosV2.get(`/document/${id}`);
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch article detail'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches document categories.
   * @returns {Promise<Category[]>} Array of document categories.
   * @throws {Error} If the API request fails.
   */
  const fetchCategories = async (): Promise<Category[]> => {
    try {
      const res = await axios.get(`/lov/document-categories`, {
        baseURL: BASE_URL_V2
      });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || res?.statusText || 'Failed to fetch categories'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches a file URL by filename.
   * @param {string} fileName - The filename to fetch URL for.
   * @returns {Promise<string>} The file URL.
   * @throws {Error} If the API request fails.
   */
  const fetchFileUrl = async (fileName: string): Promise<string> => {
    try {
      const res = await axiosV2.get(`/file/url`, {
        params: { filename: fileName }
      });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch file url'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches all tags.
   * @returns {Promise<Tag[]>} Array of tags.
   * @throws {Error} If the API request fails.
   */
  const getTags = async (): Promise<Tag[]> => {
    try {
      const res = await axios.get(`/api/tags`);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch tags'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Adds a new article with file upload.
   * @param {FormData} payload - Form data containing file and document info.
   * @returns {Promise<Article>} The created article.
   * @throws {Error} If the API request fails.
   */
  const addArticle = async (payload: FormData): Promise<Article> => {
    try {
      const res = await axiosV2.post(`/document`, payload, {
        transformRequest: () => payload
      });
      if (res?.status === 201) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to add article'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Updates an article.
   * @param {number} id - The article id.
   * @param {Partial<AddArticlePayload>} payload - Article update payload.
   * @returns {Promise<Article>} The updated article.
   * @throws {Error} If the API request fails.
   */
  const editArticle = async (id: number, payload: Partial<AddArticlePayload>): Promise<Article> => {
    try {
      const res = await axiosV2.patch(`/document/${id}`, payload);
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to edit article'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Updates the publication date of an article.
   * @param {number} id - The article id.
   * @param {object} payload - Publication date payload.
   * @param {string} [payload.publicationDate] - The publication date in ISO 8601 format.
   * @returns {Promise<Article>} The updated article.
   * @throws {Error} If the API request fails.
   */
  const editArticlePublicationDate = async (
    id: number,
    payload: {
      publicationDate: string | undefined;
    }
  ): Promise<Article> => {
    try {
      // Ensure payload is properly formatted
      const requestPayload = {
        publicationDate: payload.publicationDate
      };
      const res = await axiosV2.patch(`/document/${id}/publication-date`, requestPayload);
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to edit article publication date'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Searches for articles by query string.
   * @param {string} str - The search query string.
   * @returns {Promise<NpiArticle[]>} Array of matching articles.
   * @throws {Error} If the API request fails.
   */
  const searchArticle = async (str: string): Promise<NpiArticle[]> => {
    try {
      const res = await axiosV2.get(`/document/search`, {
        params: { str }
      });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to search article'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Deletes an article.
   * @param {number} id - The article id to delete.
   * @returns {Promise<Article>} The deleted article data.
   * @throws {Error} If the API request fails.
   */
  const deleteArticle = async (id: number): Promise<Article> => {
    try {
      const res = await axiosV2.delete(`/document/${id}`);
      if (res?.status === 200 || res?.status === 204) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to delete article'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    fetchArticles,
    fetchArticleDetail,
    fetchCategories,
    fetchFileUrl,
    getTags,
    addArticle,
    editArticle,
    editArticlePublicationDate,
    searchArticle,
    deleteArticle
  };
};

export const useDeleteArticle = (config = {}) => {
  const { deleteArticle } = useDocumentApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const article = await deleteArticle(id);
      await queryClient.invalidateQueries(['articles']);
      return article;
    },
    ...config
  });
};

export const useEditArticlePublicationDate = (config = {}) => {
  const { editArticlePublicationDate } = useDocumentApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { publicationDate: string | undefined } }) => {
      const article = await editArticlePublicationDate(id, data);
      await queryClient.invalidateQueries(['articles']);
      return article;
    },
    ...config
  });
};

export const useEditArticle = (config = {}) => {
  const { editArticle } = useDocumentApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<AddArticlePayload> }) => {
      const article = await editArticle(id, data);
      await queryClient.invalidateQueries(['articles']);
      return article;
    },
    ...config
  });
};

export const useAddArticle = (config = {}) => {
  const { addArticle } = useDocumentApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FormData) => {
      const article = await addArticle(data);
      await queryClient.invalidateQueries(['articles']);
      return article;
    },
    ...config
  });
};

export const useTags = (config = {}): UseQueryResult<Tag[], AxiosError> => {
  const { getTags } = useDocumentApi();
  return useQuery({
    queryKey: ['documentTags'],
    queryFn: async (): Promise<Tag[]> => {
      try {
        return await getTags();
      } catch (err) {
        console.error('Error occurred while fetching tags', err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 60 * 24,
    ...config
  });
};

export const useFileUrl = (fileName: string, config = {}): UseQueryResult<string, AxiosError> => {
  const { fetchFileUrl } = useDocumentApi();
  return useQuery({
    queryKey: ['fileUrl', fileName],
    queryFn: () => fetchFileUrl(fileName),
    staleTime: 1000 * 60 * 29, // half hr - 1 min to be safe
    ...config
  });
};

export const useAllCategories = (config = {}): UseQueryResult<Category[], AxiosError> => {
  const { fetchCategories } = useDocumentApi();
  return useQuery({
    queryKey: ['categories'],
    queryFn: async (): Promise<Category[]> => {
      try {
        return await fetchCategories();
      } catch (err) {
        console.error('Error occurred while fetching categories', err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 60 * 24,
    retry: false,
    ...config
  });
};

export const useArticle = (id: number, config = {}): UseQueryResult<Article, AxiosError> => {
  const { fetchArticleDetail } = useDocumentApi();
  return useQuery({
    queryKey: ['articles', id],
    queryFn: () => fetchArticleDetail(id),
    staleTime: 1000 * 60 * 60 * 24,
    ...config
  });
};

export const useFetchArticles = (config = {}): UseQueryResult<Article[], AxiosError> => {
  const { fetchArticles } = useDocumentApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['articles'],
    queryFn: async (): Promise<Article[]> => {
      const articles = await fetchArticles();
      articles.forEach((article) => {
        queryClient.setQueryData(['articles', article.id], article);
      });

      return articles;
    },
    staleTime: 1000 * 60 * 60 * 24,
    ...config
  });
};
